import React, { useCallback, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';
import { Button } from '@/shared/components/ui/Button';

// DS (F84): área de arrastar-e-soltar unificada — estilo B (border-2 dashed rounded-lg py-6,
// ícone 32px vazio / 24px compacto, spinner no upload) + botão explícito opcional (variante D).
// O componente NÃO faz upload — só entrega File[] via onFiles; o consumidor controla `uploading`.

export interface FileDropzoneProps {
  /** Tipos aceitos (atributo `accept` do input; também filtra arquivos soltos via drag&drop) */
  accept?: string;
  /** Permite múltiplos arquivos. Default: false (entrega só o primeiro) */
  multiple?: boolean;
  /** Callback com os arquivos selecionados/soltos (já filtrados por `accept`) */
  onFiles: (files: File[]) => void;
  /** Estado de envio (controlado pelo consumidor) — mostra spinner e bloqueia interação */
  uploading?: boolean;
  /** Dica de formatos exibida abaixo do texto (ex.: "JPG, PNG, WebP · máx. 10MB") */
  hint?: string;
  /** Compacto: ícone 24px + padding reduzido (ex.: "adicionar mais" abaixo de um preview) */
  compact?: boolean;
  /** Exibe botão explícito "Selecionar arquivo" (Button secondary sm) */
  showButton?: boolean;
  /** Texto principal. Default: "Clique ou arraste arquivos aqui" */
  label?: string;
  /** Rótulo do botão explícito. Default: "Selecionar arquivo" */
  buttonLabel?: string;
  /** Ícone do estado ocioso. Default: 'upload' */
  icon?: IconName;
  /** Desabilita a área por completo (sem spinner) */
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
}

// Filtra arquivo contra o padrão `accept` do input (extensão, mime exato ou wildcard "image/*").
// Necessário porque drag&drop não respeita o atributo `accept` nativamente.
const matchesAccept = (file: File, accept?: string): boolean => {
  if (!accept) return true;
  const patterns = accept
    .split(',')
    .map((p) => p.trim().toLowerCase())
    .filter(Boolean);
  if (patterns.length === 0) return true;
  const name = file.name.toLowerCase();
  const type = file.type.toLowerCase();
  return patterns.some((pattern) => {
    if (pattern.startsWith('.')) return name.endsWith(pattern);
    if (pattern.endsWith('/*')) return type.startsWith(pattern.slice(0, -1));
    return type === pattern;
  });
};

export const FileDropzone: React.FC<FileDropzoneProps> = ({
  accept,
  multiple = false,
  onFiles,
  uploading = false,
  hint,
  compact = false,
  showButton = false,
  label = 'Clique ou arraste arquivos aqui',
  buttonLabel = 'Selecionar arquivo',
  icon = 'upload',
  disabled = false,
  className,
  'data-testid': testId,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [hovered, setHovered] = useState(false);

  const blocked = uploading || disabled;
  const highlighted = !blocked && (dragOver || hovered);
  const iconSize = compact ? 'w-6 h-6' : 'w-8 h-8';

  const emitFiles = useCallback(
    (list: FileList | File[]) => {
      const accepted = Array.from(list).filter((f) => matchesAccept(f, accept));
      if (accepted.length === 0) return;
      onFiles(multiple ? accepted : accepted.slice(0, 1));
    },
    [accept, multiple, onFiles]
  );

  const openPicker = () => {
    if (!blocked) inputRef.current?.click();
  };

  return (
    <div
      role="button"
      tabIndex={blocked ? -1 : 0}
      aria-disabled={blocked || undefined}
      data-testid={testId}
      onClick={openPicker}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openPicker();
        }
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onDragOver={(e) => {
        e.preventDefault();
        if (!blocked) setDragOver(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        setDragOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        if (!blocked && e.dataTransfer.files.length > 0) emitFiles(e.dataTransfer.files);
      }}
      className={clsx(
        'flex flex-col items-center justify-center text-center rounded-lg border-2 border-dashed',
        'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring-color)]',
        compact ? 'py-4 px-4 gap-1.5' : 'py-6 px-4 gap-2',
        blocked ? 'cursor-not-allowed' : 'cursor-pointer',
        uploading && 'opacity-70',
        disabled && 'opacity-50',
        className
      )}
      style={{
        borderColor: highlighted ? 'var(--theme-primary)' : 'var(--theme-input-border)',
        backgroundColor: highlighted ? 'var(--theme-primary-background)' : 'transparent',
        color: 'var(--theme-muted-text)',
      }}
    >
      {uploading ? (
        <>
          <Icon name="loader" className={clsx('animate-spin opacity-60', iconSize)} />
          <span className="text-sm font-medium">Enviando...</span>
        </>
      ) : (
        <>
          <Icon name={icon} className={clsx('opacity-40', iconSize)} />
          <span className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>
            {label}
          </span>
          {hint && <span className="text-xs opacity-60">{hint}</span>}
          {showButton && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="mt-1"
              disabled={blocked}
              data-testid={testId ? `${testId}-button` : undefined}
              onClick={(e) => {
                e.stopPropagation();
                openPicker();
              }}
            >
              {buttonLabel}
            </Button>
          )}
        </>
      )}

      {/* Input escondido — stopPropagation evita loop: o click programático borbulharia ao container */}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        data-testid={testId ? `${testId}-input` : undefined}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          if (e.target.files && e.target.files.length > 0) emitFiles(e.target.files);
          e.target.value = '';
        }}
      />
    </div>
  );
};

export default FileDropzone;
